import type { SpaceTemplateData } from "@shared/schema";

export interface FlagshipTemplate {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  color: string;
  category: string;
  tier: "free" | "pro" | "premium";
  isFeatured: boolean;
  templateData: SpaceTemplateData;
}

export const FLAGSHIP_TEMPLATES: FlagshipTemplate[] = [
  {
    slug: "bible-study-guide",
    name: "DEEPLY SMART Bible Study Guide",
    tagline: "Deepen your faith through structured Scripture study",
    description: "A comprehensive Bible study companion with guided reading plans, verse analysis, prayer journaling, and theological exploration. Perfect for personal devotions or group study leadership.",
    icon: "BookOpen",
    color: "bg-amber-600",
    category: "Spiritual Growth",
    tier: "free",
    isFeatured: true,
    templateData: {
      topics: [
        { name: "Scripture Study", icon: "BookOpen", description: "Daily readings and verse-by-verse analysis" },
        { name: "Prayer Journal", icon: "Heart", description: "Track prayers, answers, and spiritual insights" },
        { name: "Character Studies", icon: "Users", description: "Deep dives into Biblical figures and their lessons" },
        { name: "Sermon Notes", icon: "FileText", description: "Capture and reflect on teachings" },
        { name: "Theological Topics", icon: "Lightbulb", description: "Explore doctrine and theology" },
        { name: "Memory Verses", icon: "Brain", description: "Verses to memorize and meditate on" },
      ],
      agents: [
        {
          name: "Bible Scholar",
          description: "Expert in Biblical interpretation, history, and theology",
          persona: "A thoughtful Biblical scholar with deep knowledge of Scripture, historical context, and various theological perspectives. Patient, encouraging, and always pointing back to the text.",
          systemPrompt: "You are a knowledgeable Bible scholar assistant. Help users understand Scripture through historical context, original languages (Hebrew/Greek insights when helpful), cross-references, and practical application. Be respectful of different denominational perspectives while grounding everything in the Biblical text. Encourage deeper study and personal reflection.",
          capabilities: ["Verse analysis", "Cross-referencing", "Historical context", "Practical application"],
          icon: "GraduationCap"
        },
        {
          name: "Prayer Guide",
          description: "Helps structure and deepen your prayer life",
          persona: "A gentle, spiritually mature guide who helps users develop a richer prayer life through various prayer methods and Scripture-based prayers.",
          systemPrompt: "You are a prayer guide assistant. Help users develop their prayer life through structured approaches (ACTS - Adoration, Confession, Thanksgiving, Supplication), lectio divina, praying Scripture, and journaling prompts. Be encouraging and help users see prayer as conversation with God, not performance.",
          capabilities: ["Prayer structures", "Scripture prayers", "Journaling prompts", "Meditation guides"],
          icon: "Heart"
        }
      ],
      prompts: [
        {
          name: "Verse Deep Dive",
          description: "Analyze a Bible verse in depth",
          template: "Help me study {{verse_reference}} in depth. Provide:\n1. The verse in context (surrounding passages)\n2. Key words and their original language meaning\n3. Historical/cultural background\n4. Cross-references to related passages\n5. Practical application for today",
          category: "Study",
          variables: ["verse_reference"],
          icon: "Search"
        },
        {
          name: "Character Study",
          description: "Explore a Biblical character's life and lessons",
          template: "Guide me through a character study of {{character_name}}. Include:\n1. Key life events and Scripture references\n2. Character strengths and weaknesses\n3. Relationship with God\n4. Key lessons for my life\n5. Questions for reflection",
          category: "Study",
          variables: ["character_name"],
          icon: "User"
        },
        {
          name: "Theme Explorer",
          description: "Trace a theme through Scripture",
          template: "Help me trace the theme of {{theme}} through the Bible. Show me:\n1. Old Testament foundations\n2. New Testament fulfillment\n3. Key passages to study\n4. How this theme applies to daily life",
          category: "Study",
          variables: ["theme"],
          icon: "Compass"
        },
        {
          name: "Prayer Prompt",
          description: "Generate a structured prayer based on Scripture",
          template: "Based on {{scripture_or_topic}}, help me pray using the ACTS format:\n- Adoration: Praising God for who He is\n- Confession: Acknowledging areas to grow\n- Thanksgiving: Gratitude for His blessings\n- Supplication: Requests for myself and others",
          category: "Prayer",
          variables: ["scripture_or_topic"],
          icon: "Heart"
        },
        {
          name: "Sermon Notes Template",
          description: "Structure notes from a sermon or teaching",
          template: "Help me organize my notes from a sermon on {{topic_or_passage}}:\n1. Main Scripture passage\n2. Key points (3-5)\n3. Supporting verses\n4. Personal application\n5. Action steps for this week\n6. Questions to explore further",
          category: "Notes",
          variables: ["topic_or_passage"],
          icon: "FileText"
        }
      ],
      sources: [
        { title: "Getting Started Guide", type: "guide", content: "Welcome to your Bible Study Guide! Start by choosing a book of the Bible or a reading plan." },
        { title: "Study Methods Overview", type: "document", content: "Learn about inductive study, lectio divina, and other approaches." }
      ],
      tags: [
        { name: "Old Testament", color: "bg-amber-500" },
        { name: "New Testament", color: "bg-blue-500" },
        { name: "Gospel", color: "bg-red-500" },
        { name: "Wisdom", color: "bg-purple-500" },
        { name: "Prophecy", color: "bg-green-500" },
        { name: "Memorize", color: "bg-pink-500" }
      ],
      lens: {
        purpose: "Spiritual growth through Scripture study and prayer",
        priorities: ["Daily Bible reading", "Prayer life development", "Theological understanding", "Practical application"],
        details: "Systematically study the Bible, develop consistent prayer habits, and apply Biblical wisdom to daily life."
      },
      goals: [
        "Complete a book study",
        "Memorize 10 key verses",
        "Establish daily prayer routine",
        "Study 5 Biblical characters"
      ]
    }
  },
  {
    slug: "kitchen-commander",
    name: "DEEPLY SMART Kitchen Commander",
    tagline: "Master your culinary kingdom with AI-powered meal planning",
    description: "Your personal sous chef for recipe management, meal planning, grocery lists, and cooking technique mastery. From weeknight dinners to impressive feasts, Kitchen Commander has you covered.",
    icon: "ChefHat",
    color: "bg-orange-500",
    category: "Lifestyle",
    tier: "free",
    isFeatured: true,
    templateData: {
      topics: [
        { name: "Recipe Collection", icon: "BookOpen", description: "Your personal cookbook of saved recipes" },
        { name: "Meal Plans", icon: "Calendar", description: "Weekly and monthly meal planning" },
        { name: "Shopping Lists", icon: "ShoppingCart", description: "Organized grocery lists by store section" },
        { name: "Cooking Techniques", icon: "Flame", description: "Master fundamental and advanced techniques" },
        { name: "Ingredient Guide", icon: "Apple", description: "Substitutions, storage, and seasonal produce" },
        { name: "Kitchen Tips", icon: "Lightbulb", description: "Time-saving hacks and equipment guides" },
      ],
      agents: [
        {
          name: "Chef AI",
          description: "Your personal culinary assistant and recipe advisor",
          persona: "An experienced, enthusiastic chef who loves teaching and makes cooking accessible. Equal parts professional expertise and home cook practicality.",
          systemPrompt: "You are Chef AI, a knowledgeable culinary assistant. Help users with recipes, cooking techniques, ingredient substitutions, and meal planning. Be encouraging to beginners while offering advanced tips to experienced cooks. Consider dietary restrictions, budget, and time constraints. Provide clear, step-by-step instructions and explain the 'why' behind techniques.",
          capabilities: ["Recipe creation", "Technique guidance", "Substitutions", "Troubleshooting"],
          icon: "ChefHat"
        },
        {
          name: "Meal Planner",
          description: "Strategic meal planning and grocery optimization",
          persona: "An organized, practical assistant focused on making weekly meal planning efficient and reducing food waste.",
          systemPrompt: "You are a meal planning assistant. Help users plan balanced, varied meals for the week considering their schedule, preferences, and budget. Suggest recipes that share ingredients to minimize waste. Create organized shopping lists grouped by store section. Consider prep-ahead opportunities and leftover utilization.",
          capabilities: ["Weekly planning", "Grocery lists", "Budget optimization", "Prep scheduling"],
          icon: "Calendar"
        }
      ],
      prompts: [
        {
          name: "Recipe from Ingredients",
          description: "Create a recipe from what you have",
          template: "I have these ingredients: {{ingredients}}. Suggest {{number_of_recipes}} recipes I can make. Consider:\n- Cuisine preference: {{cuisine_preference}}\n- Time available: {{time_available}}\n- Skill level: {{skill_level}}",
          category: "Recipes",
          variables: ["ingredients", "number_of_recipes", "cuisine_preference", "time_available", "skill_level"],
          icon: "Sparkles"
        },
        {
          name: "Weekly Meal Plan",
          description: "Generate a complete weekly meal plan",
          template: "Create a weekly meal plan for {{number_of_people}} people. Preferences:\n- Dietary restrictions: {{dietary_restrictions}}\n- Budget level: {{budget_level}}\n- Cooking time per meal: {{cooking_time}}\n- Include: breakfasts, lunches, dinners, and 2 snacks\nProvide a consolidated shopping list organized by grocery section.",
          category: "Planning",
          variables: ["number_of_people", "dietary_restrictions", "budget_level", "cooking_time"],
          icon: "Calendar"
        },
        {
          name: "Recipe Scaling",
          description: "Scale a recipe up or down",
          template: "Help me scale this recipe from {{original_servings}} servings to {{target_servings}} servings:\n\n{{recipe_details}}\n\nProvide the adjusted quantities and any tips for cooking at the new scale.",
          category: "Recipes",
          variables: ["original_servings", "target_servings", "recipe_details"],
          icon: "Scale"
        },
        {
          name: "Technique Tutorial",
          description: "Learn a cooking technique step-by-step",
          template: "Teach me how to {{technique}} like a professional chef. Include:\n1. What equipment I need\n2. Step-by-step instructions\n3. Common mistakes to avoid\n4. How to know when it's done right\n5. Recipes to practice this technique",
          category: "Learning",
          variables: ["technique"],
          icon: "GraduationCap"
        },
        {
          name: "Ingredient Substitution",
          description: "Find substitutes for missing ingredients",
          template: "I'm making {{dish_name}} but I don't have {{missing_ingredient}}. What can I substitute? Consider:\n- Flavor profile\n- Texture\n- Cooking behavior\n- Dietary alternatives (vegan, allergy-friendly)",
          category: "Tips",
          variables: ["dish_name", "missing_ingredient"],
          icon: "RefreshCw"
        }
      ],
      tags: [
        { name: "Quick & Easy", color: "bg-green-500" },
        { name: "Meal Prep", color: "bg-blue-500" },
        { name: "Special Occasion", color: "bg-purple-500" },
        { name: "Budget Friendly", color: "bg-emerald-500" },
        { name: "Healthy", color: "bg-teal-500" },
        { name: "Comfort Food", color: "bg-orange-500" }
      ],
      lens: {
        purpose: "Culinary mastery and kitchen efficiency",
        priorities: ["Recipe organization", "Meal planning", "Technique development", "Waste reduction"],
        details: "Build your personal cookbook, plan balanced meals, and continuously develop your cooking skills."
      },
      goals: [
        "Build recipe collection of 50+ recipes",
        "Plan meals for 4 consecutive weeks",
        "Master 10 fundamental techniques",
        "Reduce food waste by 50%"
      ]
    }
  },
  {
    slug: "business-pro",
    name: "DEEPLY SMART Business Pro",
    tagline: "Strategic intelligence for business growth",
    description: "Your AI-powered business intelligence hub for market research, competitor analysis, strategic planning, and decision-making. From startup founders to executives, gain the insights you need.",
    icon: "Briefcase",
    color: "bg-slate-700",
    category: "Business",
    tier: "pro",
    isFeatured: true,
    templateData: {
      topics: [
        { name: "Market Research", icon: "TrendingUp", description: "Industry trends and market analysis" },
        { name: "Competitor Intel", icon: "Target", description: "Competitive landscape and positioning" },
        { name: "Strategy", icon: "Map", description: "Strategic initiatives and business plans" },
        { name: "Metrics & KPIs", icon: "BarChart", description: "Track key performance indicators" },
        { name: "Meeting Notes", icon: "Users", description: "Client and team meeting documentation" },
        { name: "Decision Log", icon: "CheckSquare", description: "Track decisions and their outcomes" },
      ],
      agents: [
        {
          name: "Strategy Advisor",
          description: "Strategic business consultant and analyst",
          persona: "A seasoned business strategist with experience across industries, combining analytical rigor with practical execution focus.",
          systemPrompt: "You are a strategic business advisor. Help users analyze markets, evaluate opportunities, and develop actionable strategies. Use frameworks like SWOT, Porter's Five Forces, and Blue Ocean Strategy when appropriate. Be direct and focused on actionable insights. Challenge assumptions constructively and help identify blind spots.",
          capabilities: ["Market analysis", "Strategic frameworks", "Opportunity evaluation", "Risk assessment"],
          icon: "Lightbulb"
        },
        {
          name: "Research Analyst",
          description: "Deep research and competitive intelligence",
          persona: "A thorough researcher who digs deep into data, trends, and competitive dynamics to surface actionable intelligence.",
          systemPrompt: "You are a business research analyst. Help users conduct thorough market research, analyze competitors, and synthesize information from multiple sources. Present findings clearly with supporting evidence. Identify patterns, trends, and implications for the user's business.",
          capabilities: ["Competitive analysis", "Market sizing", "Trend identification", "Report synthesis"],
          icon: "Search"
        }
      ],
      prompts: [
        {
          name: "SWOT Analysis",
          description: "Comprehensive SWOT for a business or initiative",
          template: "Conduct a SWOT analysis for {{business_or_initiative}}:\n\nContext: {{context}}\n\nProvide:\n1. Strengths (internal positives)\n2. Weaknesses (internal challenges)\n3. Opportunities (external positives)\n4. Threats (external challenges)\n5. Strategic recommendations based on the analysis",
          category: "Strategy",
          variables: ["business_or_initiative", "context"],
          icon: "Target"
        },
        {
          name: "Competitor Deep Dive",
          description: "Analyze a specific competitor",
          template: "Analyze {{competitor_name}} as a competitor to {{my_business}}:\n1. Their value proposition and positioning\n2. Strengths and weaknesses\n3. Pricing and business model\n4. Target customer segments\n5. Recent moves and likely strategy\n6. How we can differentiate",
          category: "Competitive",
          variables: ["competitor_name", "my_business"],
          icon: "Eye"
        },
        {
          name: "Market Opportunity",
          description: "Evaluate a market opportunity",
          template: "Evaluate the market opportunity for {{opportunity}}:\n1. Market size and growth rate\n2. Customer segments and needs\n3. Competitive landscape\n4. Barriers to entry\n5. Key success factors\n6. Risks and mitigations\n7. Go/No-Go recommendation",
          category: "Analysis",
          variables: ["opportunity"],
          icon: "TrendingUp"
        },
        {
          name: "Decision Framework",
          description: "Structure a complex business decision",
          template: "Help me decide: {{decision_question}}\n\nOptions being considered:\n{{options}}\n\nKey criteria: {{criteria}}\n\nProvide a structured analysis with pros/cons, risks, and a recommendation.",
          category: "Decisions",
          variables: ["decision_question", "options", "criteria"],
          icon: "GitBranch"
        }
      ],
      tags: [
        { name: "High Priority", color: "bg-red-500" },
        { name: "Research", color: "bg-blue-500" },
        { name: "Action Required", color: "bg-orange-500" },
        { name: "Completed", color: "bg-green-500" },
        { name: "Quarterly Review", color: "bg-purple-500" }
      ],
      lens: {
        purpose: "Strategic business intelligence and growth",
        priorities: ["Market insights", "Competitive positioning", "Data-driven decisions", "Strategic execution"],
        details: "Research markets, track competitors, and make informed strategic decisions with AI-powered analysis."
      },
      goals: [
        "Complete competitive landscape analysis",
        "Define 3-year strategic plan",
        "Establish KPI dashboard",
        "Conduct quarterly business reviews"
      ]
    }
  },
  {
    slug: "student-edition",
    name: "DEEPLY SMART Student Edition",
    tagline: "Ace your academics with AI-powered study tools",
    description: "The ultimate academic companion for students. Organize courses, synthesize research, create study guides, and prepare for exams with AI assistance that adapts to your learning style.",
    icon: "GraduationCap",
    color: "bg-blue-600",
    category: "Education",
    tier: "free",
    isFeatured: true,
    templateData: {
      topics: [
        { name: "Course Notes", icon: "BookOpen", description: "Organized notes by course and topic" },
        { name: "Research Papers", icon: "FileText", description: "Papers, sources, and citations" },
        { name: "Study Guides", icon: "Brain", description: "Condensed study materials and summaries" },
        { name: "Projects", icon: "Folder", description: "Group and individual project work" },
        { name: "Exam Prep", icon: "Target", description: "Test preparation and practice" },
        { name: "Resources", icon: "Link", description: "Textbooks, videos, and helpful links" },
      ],
      agents: [
        {
          name: "Study Buddy",
          description: "Your AI tutor for understanding difficult concepts",
          persona: "A patient, encouraging tutor who explains complex concepts in multiple ways until they click. Celebrates progress and keeps study sessions engaging.",
          systemPrompt: "You are a helpful study tutor. Explain concepts clearly using analogies, examples, and different approaches based on what the student needs. Break complex topics into digestible parts. Ask questions to check understanding. Be encouraging and patient. Help students develop metacognitive skills - learning how to learn.",
          capabilities: ["Concept explanation", "Practice problems", "Study techniques", "Comprehension checks"],
          icon: "MessageCircle"
        },
        {
          name: "Research Assistant",
          description: "Helps with papers, citations, and academic writing",
          persona: "A thorough research assistant who helps students find, evaluate, and synthesize sources while maintaining academic integrity.",
          systemPrompt: "You are an academic research assistant. Help students develop thesis statements, find and evaluate sources, organize arguments, and improve their academic writing. Teach proper citation practices. Help with outlines and drafts. Never write papers for students - instead, guide them to develop their own ideas and writing skills.",
          capabilities: ["Thesis development", "Source evaluation", "Outline creation", "Writing feedback"],
          icon: "Search"
        }
      ],
      prompts: [
        {
          name: "Explain Like I'm New",
          description: "Get a beginner-friendly explanation",
          template: "Explain {{concept}} as if I'm completely new to {{subject}}. Use:\n1. A simple definition\n2. A real-world analogy\n3. A concrete example\n4. Why it matters\n5. How it connects to other concepts",
          category: "Learning",
          variables: ["concept", "subject"],
          icon: "Lightbulb"
        },
        {
          name: "Study Guide Creator",
          description: "Generate a study guide for an exam",
          template: "Create a comprehensive study guide for {{course_or_topic}}. Cover:\n1. Key concepts and definitions\n2. Important formulas/theories\n3. Common exam questions\n4. Memory tricks and mnemonics\n5. Practice problems with solutions",
          category: "Exam Prep",
          variables: ["course_or_topic"],
          icon: "FileText"
        },
        {
          name: "Paper Outline",
          description: "Structure a research paper",
          template: "Help me outline a paper on {{topic}} for {{course}}.\n- Length: {{page_count}} pages\n- Required sources: {{source_count}}\n\nProvide:\n1. Potential thesis statements\n2. Detailed outline with sections\n3. What evidence each section needs\n4. Transition suggestions",
          category: "Writing",
          variables: ["topic", "course", "page_count", "source_count"],
          icon: "List"
        },
        {
          name: "Quiz Me",
          description: "Generate practice questions",
          template: "Create {{number}} practice questions on {{topic}} at {{difficulty}} level.\nInclude:\n- Multiple choice questions\n- Short answer questions\n- One essay question\n\nProvide answers and explanations after each question.",
          category: "Exam Prep",
          variables: ["number", "topic", "difficulty"],
          icon: "HelpCircle"
        }
      ],
      tags: [
        { name: "Exam", color: "bg-red-500" },
        { name: "Assignment", color: "bg-blue-500" },
        { name: "Reading", color: "bg-green-500" },
        { name: "Project", color: "bg-purple-500" },
        { name: "Review", color: "bg-orange-500" },
        { name: "Done", color: "bg-gray-500" }
      ],
      lens: {
        purpose: "Academic excellence through organized, AI-enhanced study",
        priorities: ["Course organization", "Deep understanding", "Effective study habits", "Research skills"],
        details: "Organize your academic life, understand complex material, and develop skills that last beyond graduation."
      },
      goals: [
        "Organize all current courses",
        "Create study guides for each class",
        "Develop consistent study routine",
        "Improve research and writing skills"
      ]
    }
  },
  {
    slug: "health-wellness",
    name: "DEEPLY SMART Health & Wellness",
    tagline: "Your holistic health companion",
    description: "Track your wellness journey across physical fitness, nutrition, mental health, and lifestyle. Set goals, log progress, and get personalized insights to become the healthiest version of yourself.",
    icon: "Heart",
    color: "bg-green-600",
    category: "Health",
    tier: "free",
    isFeatured: true,
    templateData: {
      topics: [
        { name: "Fitness", icon: "Dumbbell", description: "Workout plans and exercise tracking" },
        { name: "Nutrition", icon: "Apple", description: "Meal tracking and dietary insights" },
        { name: "Mental Wellness", icon: "Brain", description: "Mindfulness, stress, and mental health" },
        { name: "Sleep", icon: "Moon", description: "Sleep quality and recovery" },
        { name: "Goals & Progress", icon: "Target", description: "Health goals and milestone tracking" },
        { name: "Research", icon: "BookOpen", description: "Health topics and evidence-based info" },
      ],
      agents: [
        {
          name: "Wellness Coach",
          description: "Your supportive health and fitness advisor",
          persona: "An encouraging, knowledgeable wellness coach who helps you make sustainable lifestyle changes without judgment. Focuses on progress over perfection.",
          systemPrompt: "You are a supportive wellness coach. Help users set realistic health goals, develop sustainable habits, and stay motivated. Provide evidence-based advice while respecting that you're not a medical professional (encourage consulting doctors for medical concerns). Focus on holistic wellness - physical, mental, and emotional. Celebrate small wins and help users recover from setbacks.",
          capabilities: ["Goal setting", "Habit building", "Motivation", "Progress tracking"],
          icon: "Heart"
        },
        {
          name: "Nutrition Guide",
          description: "Balanced nutrition and meal planning advice",
          persona: "A practical nutrition advisor focused on sustainable, enjoyable eating rather than restrictive diets.",
          systemPrompt: "You are a nutrition guide. Help users understand balanced nutrition, plan satisfying meals, and develop a healthy relationship with food. Avoid promoting restrictive diets or obsessive tracking. Focus on adding nutritious foods rather than elimination. Consider cultural preferences, budget, and practicality. Always recommend consulting a dietitian for specific medical nutrition needs.",
          capabilities: ["Meal ideas", "Nutrition education", "Habit tips", "Recipe suggestions"],
          icon: "Apple"
        }
      ],
      prompts: [
        {
          name: "Wellness Check-In",
          description: "Reflect on your overall wellness",
          template: "Help me do a wellness check-in. I'll rate each area 1-10:\n- Physical energy: {{physical}}\n- Mental clarity: {{mental}}\n- Emotional state: {{emotional}}\n- Sleep quality: {{sleep}}\n- Stress level: {{stress}}\n\nWhat patterns do you notice? What's one small improvement I could make?",
          category: "Tracking",
          variables: ["physical", "mental", "emotional", "sleep", "stress"],
          icon: "ClipboardCheck"
        },
        {
          name: "Workout Plan",
          description: "Create a personalized workout routine",
          template: "Create a workout plan for me:\n- Goal: {{fitness_goal}}\n- Available time: {{time_per_session}} minutes, {{days_per_week}} days/week\n- Equipment: {{equipment}}\n- Fitness level: {{fitness_level}}\n\nInclude warm-up, main workout, and cool-down.",
          category: "Fitness",
          variables: ["fitness_goal", "time_per_session", "days_per_week", "equipment", "fitness_level"],
          icon: "Dumbbell"
        },
        {
          name: "Healthy Meal Ideas",
          description: "Get nutritious meal suggestions",
          template: "Suggest {{meal_type}} ideas that are:\n- Prep time: {{max_prep_time}} or less\n- Dietary needs: {{dietary_needs}}\n- Goal: {{nutrition_goal}}\n\nProvide 5 options with brief recipes and nutrition highlights.",
          category: "Nutrition",
          variables: ["meal_type", "max_prep_time", "dietary_needs", "nutrition_goal"],
          icon: "UtensilsCrossed"
        },
        {
          name: "Mindfulness Moment",
          description: "Guided mindfulness or stress relief",
          template: "I'm feeling {{current_feeling}} and have {{time_available}} minutes. Guide me through:\n1. A brief mindfulness exercise\n2. Breathing technique\n3. One actionable thing I can do right now to feel better",
          category: "Mental",
          variables: ["current_feeling", "time_available"],
          icon: "Flower"
        }
      ],
      tags: [
        { name: "Cardio", color: "bg-red-500" },
        { name: "Strength", color: "bg-blue-500" },
        { name: "Flexibility", color: "bg-purple-500" },
        { name: "Nutrition", color: "bg-green-500" },
        { name: "Mental Health", color: "bg-teal-500" },
        { name: "Recovery", color: "bg-orange-500" }
      ],
      lens: {
        purpose: "Holistic health and sustainable wellness habits",
        priorities: ["Balanced fitness routine", "Nutritious eating", "Mental wellness", "Quality sleep"],
        details: "Build sustainable health habits across all dimensions of wellness for long-term vitality."
      },
      goals: [
        "Establish consistent workout routine",
        "Improve sleep quality",
        "Develop mindfulness practice",
        "Build balanced nutrition habits"
      ]
    }
  },
  {
    slug: "creative-studio",
    name: "DEEPLY SMART Creative Studio",
    tagline: "Fuel your creativity and build your portfolio",
    description: "A creative workspace for artists, writers, designers, and makers. Capture inspiration, develop projects, build your portfolio, and push your creative boundaries with AI collaboration.",
    icon: "Palette",
    color: "bg-purple-600",
    category: "Creative",
    tier: "free",
    isFeatured: true,
    templateData: {
      topics: [
        { name: "Inspiration", icon: "Sparkles", description: "Ideas, mood boards, and creative sparks" },
        { name: "Projects", icon: "Folder", description: "Active creative projects and works" },
        { name: "Portfolio", icon: "Image", description: "Completed works and showcase pieces" },
        { name: "Techniques", icon: "Wrench", description: "Skills, tutorials, and craft development" },
        { name: "References", icon: "Link", description: "Artists, works, and resources that inspire" },
        { name: "Experiments", icon: "Flask", description: "Creative experiments and explorations" },
      ],
      agents: [
        {
          name: "Creative Partner",
          description: "Your AI collaborator for ideation and development",
          persona: "An imaginative creative partner who helps brainstorm, iterate, and push ideas further. Encouraging but also willing to offer constructive critique.",
          systemPrompt: "You are a creative collaborator. Help users brainstorm ideas, develop concepts, overcome creative blocks, and refine their work. Ask probing questions to help them discover their unique vision. Offer multiple directions and alternatives. Be encouraging while also providing honest, constructive feedback when asked. Respect that the user is the creative lead - your role is to support their vision.",
          capabilities: ["Brainstorming", "Concept development", "Creative critique", "Inspiration"],
          icon: "Lightbulb"
        },
        {
          name: "Writing Coach",
          description: "Helps with all forms of creative writing",
          persona: "A supportive writing mentor who helps develop voice, structure, and craft while preserving the writer's unique style.",
          systemPrompt: "You are a creative writing coach. Help with fiction, poetry, screenwriting, copywriting, and other creative writing. Assist with plot development, character creation, dialogue, pacing, and style. Provide specific, actionable feedback. Help overcome writer's block with prompts and exercises. Never rewrite their work - instead, guide them to find their own solutions.",
          capabilities: ["Story development", "Character creation", "Writing exercises", "Editing guidance"],
          icon: "Feather"
        }
      ],
      prompts: [
        {
          name: "Creative Brainstorm",
          description: "Generate ideas for a creative project",
          template: "Help me brainstorm ideas for {{project_type}}.\nTheme/topic: {{theme}}\nMood/tone: {{mood}}\nConstraints: {{constraints}}\n\nGenerate 10 diverse ideas ranging from safe to experimental.",
          category: "Ideation",
          variables: ["project_type", "theme", "mood", "constraints"],
          icon: "Sparkles"
        },
        {
          name: "Story Starter",
          description: "Get unstuck with a creative writing prompt",
          template: "Create a compelling story starter for a {{genre}} story involving {{elements}}. Provide:\n1. An opening paragraph that hooks the reader\n2. The central conflict or question\n3. 3 possible directions the story could go\n4. A potential theme to explore",
          category: "Writing",
          variables: ["genre", "elements"],
          icon: "Feather"
        },
        {
          name: "Creative Critique",
          description: "Get constructive feedback on your work",
          template: "Please provide constructive feedback on this {{work_type}}:\n\n{{work_description}}\n\nFocus on:\n- What's working well\n- What could be stronger\n- Specific suggestions for improvement\n- Questions to consider",
          category: "Feedback",
          variables: ["work_type", "work_description"],
          icon: "MessageSquare"
        },
        {
          name: "Break the Block",
          description: "Overcome creative block",
          template: "I'm stuck on {{project}} because {{stuck_reason}}. Help me get unstuck with:\n1. A fresh perspective on the problem\n2. 3 quick creative exercises\n3. An unconventional approach I haven't considered\n4. Permission to try something imperfect",
          category: "Productivity",
          variables: ["project", "stuck_reason"],
          icon: "Unlock"
        }
      ],
      tags: [
        { name: "Idea", color: "bg-yellow-500" },
        { name: "In Progress", color: "bg-blue-500" },
        { name: "Finished", color: "bg-green-500" },
        { name: "Explore Later", color: "bg-purple-500" },
        { name: "Collaboration", color: "bg-pink-500" },
        { name: "Portfolio", color: "bg-indigo-500" }
      ],
      lens: {
        purpose: "Creative expression and artistic growth",
        priorities: ["Consistent creative practice", "Skill development", "Portfolio building", "Experimentation"],
        details: "Capture inspiration, develop your craft, and build a body of work you're proud of."
      },
      goals: [
        "Maintain daily creative practice",
        "Complete 3 portfolio pieces",
        "Learn 2 new techniques",
        "Share work publicly"
      ]
    }
  }
];

export const TEMPLATE_CATEGORIES = [
  "All",
  "Spiritual Growth",
  "Lifestyle",
  "Business",
  "Education",
  "Health",
  "Creative"
];
